import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaCar,
  FaSpinner,
  FaWrench,
  FaCog,
  FaShieldAlt,
  FaExclamationTriangle,
  FaTimes,
  FaFilePdf,
  FaShareAlt,
  FaClipboard,
  FaHeartbeat,
  FaChevronRight,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useVehicle } from '../../contexts/VehicleContext';
import { passportService } from '../../services/apiService';

const EVENT_META = {
  service_visit: { label: 'Service Visit', icon: FaWrench, color: 'text-blue-600 bg-blue-50' },
  part_replaced: { label: 'Part Replaced', icon: FaCog, color: 'text-violet-600 bg-violet-50' },
  warranty_issued: { label: 'Warranty Issued', icon: FaShieldAlt, color: 'text-emerald-600 bg-emerald-50' },
  issue_flagged: { label: 'Issue Flagged', icon: FaExclamationTriangle, color: 'text-amber-600 bg-amber-50' },
};

function formatDate(date) {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function scoreStrokeColor(color) {
  if (color === 'green') return '#22c55e';
  if (color === 'amber') return '#f59e0b';
  return '#ef4444';
}

function scoreStatusLabel(color) {
  if (color === 'green') return { text: 'Good condition', className: 'bg-emerald-100 text-emerald-800' };
  if (color === 'amber') return { text: 'Fair condition', className: 'bg-amber-100 text-amber-900' };
  return { text: 'Needs attention', className: 'bg-red-100 text-red-800' };
}

function HealthGauge({ score, color, onClick, size = 108 }) {
  const stroke = 9;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative block rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
      aria-label={`Health score ${score}. Tap for breakdown.`}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={scoreStrokeColor(color)}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className="text-xl sm:text-2xl font-bold text-gray-900"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          {score}
        </motion.span>
        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Health</span>
      </div>
    </button>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl bg-white shadow-xl max-h-[92vh] sm:max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 sm:px-5 sm:py-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900 pr-2">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>
        <div className="p-4 sm:p-5">{children}</div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500">{children}</h2>
  );
}

function PassportContent({
  passport,
  readOnly = false,
  vehicles = [],
  activeVehicleId,
  onSelectVehicle,
  onExport,
  onShare,
  exporting = false,
  sharing = false,
}) {
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [eventDetail, setEventDetail] = useState(null);
  const [shareUrl, setShareUrl] = useState('');

  const v = passport?.vehicle;
  const health = passport?.health || { score: 0, color: 'red', factors: [] };
  const alerts = passport?.alerts || [];
  const timeline = passport?.timeline || [];
  const warranties = passport?.warranties || [];
  const empty = !passport?.hasGarageVisits && timeline.length === 0;
  const status = scoreStatusLabel(health.color);

  const showVehicleTabs = !readOnly && vehicles.length > 1;

  return (
    <div className="space-y-4 lg:space-y-6">
      {showVehicleTabs && (
        <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 -mx-1 px-1 scrollbar-hide">
          {vehicles.map((vehicle) => {
            const id = vehicle._id || vehicle.id;
            const active = String(id) === String(activeVehicleId);
            const label =
              vehicle.nickname ||
              [vehicle.make, vehicle.model].filter(Boolean).join(' ') ||
              'Vehicle';
            return (
              <button
                key={id}
                type="button"
                onClick={() => onSelectVehicle?.(id)}
                className={`snap-start shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs sm:text-sm font-semibold transition-all ${
                  active
                    ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <FaCar className="text-xs" />
                {label}
              </button>
            );
          })}
        </div>
      )}

      <div className={empty ? 'space-y-4' : 'grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6'}>
        <div className={empty ? 'space-y-4' : 'lg:col-span-5 space-y-4'}>
          {/* Hero — responsive vehicle + score */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 md:p-5">
              <div className="min-w-0 flex-1 order-2 sm:order-1">
                <p className="text-lg sm:text-xl font-bold text-gray-900 break-words">
                  {v?.label || 'Your vehicle'}
                </p>
                {v?.registrationNumber && (
                  <p className="mt-0.5 text-xs sm:text-sm font-mono text-gray-500 break-all">
                    {v.registrationNumber}
                  </p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-1.5 sm:gap-2">
                  {v?.variant && (
                    <span className="rounded-md bg-blue-50 px-2 py-0.5 text-[11px] sm:text-xs font-semibold text-blue-700">
                      {v.variant}
                    </span>
                  )}
                  <span className={`rounded-md px-2 py-0.5 text-[11px] sm:text-xs font-bold ${status.className}`}>
                    {status.text}
                  </span>
                  {v?.odometerKm != null && (
                    <span className="text-[11px] sm:text-xs text-gray-500">
                      {v.odometerKm.toLocaleString()} km
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setBreakdownOpen(true)}
                  className="mt-2 text-xs sm:text-sm font-medium text-blue-600 hover:underline"
                >
                  Score breakdown →
                </button>
              </div>
              <div className="flex justify-center sm:justify-end shrink-0 order-1 sm:order-2 scale-95 sm:scale-100 lg:scale-105 origin-center">
                <HealthGauge
                  score={health.score}
                  color={health.color}
                  onClick={() => setBreakdownOpen(true)}
                />
              </div>
            </div>

            {!readOnly && (
              <div className="grid grid-cols-2 border-t border-gray-100 divide-x divide-gray-100">
                <button
                  type="button"
                  onClick={onExport}
                  disabled={exporting}
                  className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  {exporting ? <FaSpinner className="animate-spin" /> : <FaFilePdf className="text-red-500" />}
                  Export PDF
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const url = await onShare?.();
                    if (url) setShareUrl(url);
                  }}
                  disabled={sharing}
                  className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  {sharing ? <FaSpinner className="animate-spin" /> : <FaShareAlt className="text-blue-500" />}
                  Share for Resale
                </button>
              </div>
            )}
          </div>

          {alerts.length > 0 && (
            <div className="space-y-2">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className="flex gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3"
                >
                  <FaExclamationTriangle className="mt-0.5 shrink-0 text-orange-500" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-orange-900">{alert.title}</p>
                    <p className="text-sm text-orange-800 break-words">{alert.message}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {shareUrl && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 sm:p-4">
              <p className="text-sm font-semibold text-blue-900">7-day read-only link</p>
              <p className="mt-1 break-all text-xs text-blue-800">{shareUrl}</p>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  toast.success('Link copied');
                }}
                className="mt-3 inline-flex w-full sm:w-auto items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
              >
                <FaClipboard /> Copy link
              </button>
            </div>
          )}

          {empty && (
            <div className="rounded-xl border border-dashed border-gray-300 bg-white p-5 sm:p-6 text-center">
              <FaWrench className="mx-auto text-3xl text-gray-300" />
              <p className="mt-3 text-base font-semibold text-gray-900">No garage visits yet</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
                History appears automatically after an AutoServ OS garage services this vehicle.
              </p>
              {!readOnly && (
                <Link
                  to="/garages"
                  className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                >
                  Find a garage
                  <FaChevronRight className="text-[10px]" />
                </Link>
              )}
            </div>
          )}
        </div>

        {!empty && (
          <div className="lg:col-span-7 space-y-4 lg:space-y-6">
            <section>
              <SectionTitle>Service timeline</SectionTitle>
              <ul className="relative mt-3 space-y-0">
                {timeline.map((event, index) => {
                  const meta = EVENT_META[event.type] || EVENT_META.service_visit;
                  const Icon = meta.icon;
                  const isLast = index === timeline.length - 1;
                  return (
                    <li key={event.id} className="relative flex gap-2 sm:gap-3 pb-4">
                      {!isLast && (
                        <span className="absolute left-4 sm:left-5 top-10 bottom-0 w-px bg-gray-200" aria-hidden />
                      )}
                      <span
                        className={`relative z-10 flex h-8 w-8 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full ${meta.color}`}
                      >
                        <Icon className="text-xs sm:text-sm" />
                      </span>
                      <button
                        type="button"
                        onClick={() => setEventDetail(event)}
                        className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white p-3 sm:p-4 text-left shadow-sm transition hover:border-gray-300"
                      >
                        <p className="text-[11px] sm:text-xs font-medium text-gray-400 break-words">
                          {formatDate(event.date)} · {event.garageCity}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-gray-900">{meta.label}</p>
                        <p className="mt-0.5 text-xs sm:text-sm text-gray-600 line-clamp-2">{event.summary}</p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>

            {warranties.length > 0 && (
              <section>
                <SectionTitle>Active warranties</SectionTitle>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                  {warranties.map((w) => (
                    <div
                      key={w.warrantyId}
                      className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                    >
                      <p className="text-sm font-semibold text-gray-900 break-words">{w.partName}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {w.grade} · expires {formatDate(w.expiresAt)}
                      </p>
                      <p className="mt-0.5 text-xs font-semibold text-emerald-600">{w.daysLeft} days left</p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {breakdownOpen && (
        <Modal title="Health score breakdown" onClose={() => setBreakdownOpen(false)}>
          <p className="mb-4 text-sm text-gray-600">
            Calculated on the server after each garage visit.
          </p>
          <ul className="space-y-3">
            {health.factors?.map((f) => (
              <li key={f.key} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                <div className="mb-2 flex items-start justify-between gap-3">
                  <span className="text-sm font-semibold text-gray-900 break-words">{f.label}</span>
                  <span className="shrink-0 text-sm font-bold text-gray-700">{f.score}/100</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full rounded-full bg-blue-500 transition-all"
                    style={{ width: `${Math.min(100, f.score)}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500">{f.detail}</p>
              </li>
            ))}
          </ul>
        </Modal>
      )}

      {eventDetail && (
        <Modal
          title={EVENT_META[eventDetail.type]?.label || 'Event details'}
          onClose={() => setEventDetail(null)}
        >
          <p className="text-sm text-gray-500">{formatDate(eventDetail.date)}</p>
          <p className="mt-2 text-base font-semibold text-gray-900">{eventDetail.summary}</p>
          <p className="mt-3 text-sm leading-relaxed text-gray-600 whitespace-pre-wrap">
            {eventDetail.detail}
          </p>
          {eventDetail.jobCardId && (
            <p className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
              Job card: {eventDetail.jobCardId}
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}

const HealthPassport = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { vehicles, selectedVehicle, fetchVehicles } = useVehicle();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing] = useState(false);

  const vehicleIdFromUrl = searchParams.get('vehicleId');
  const activeVehicleId = useMemo(() => {
    if (vehicleIdFromUrl) return vehicleIdFromUrl;
    return selectedVehicle?._id || selectedVehicle?.id || vehicles[0]?._id || vehicles[0]?.id;
  }, [vehicleIdFromUrl, selectedVehicle, vehicles]);

  const load = useCallback(async () => {
    if (!activeVehicleId) {
      setPassport(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setError('');
      const data = await passportService.getPassport(activeVehicleId);
      setPassport(data);
    } catch (err) {
      setError(err.message || 'Failed to load passport');
      setPassport(null);
    } finally {
      setLoading(false);
    }
  }, [activeVehicleId]);

  useEffect(() => {
    fetchVehicles?.();
  }, [fetchVehicles]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSelectVehicle = (id) => {
    setSearchParams({ vehicleId: id });
  };

  const handleExport = async () => {
    if (!activeVehicleId) return;
    setExporting(true);
    try {
      await passportService.exportPdf(activeVehicleId);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    if (!activeVehicleId) return null;
    setSharing(true);
    try {
      const result = await passportService.createShareLink(activeVehicleId);
      const url = result.shareUrl || `${window.location.origin}/passport/public/${result.token}`;
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied (valid 7 days)');
      return url;
    } catch (err) {
      toast.error(err.message || 'Could not create share link');
      return null;
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="checkout-light-page w-full min-h-[50vh] px-4 pt-8 pb-24 sm:px-6 sm:pt-10 md:pt-12 lg:px-8 lg:pb-12">
      <div className="mx-auto w-full max-w-6xl">
        <div className="mb-5 sm:mb-6 flex flex-wrap items-center gap-x-3 gap-y-2">
          <Link
            to="/account"
            className="shrink-0 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            ← Back
          </Link>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <FaHeartbeat className="shrink-0 text-red-500" />
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
              Car Health Passport
            </h1>
          </div>
        </div>

        {!activeVehicleId && !loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm">
            <FaCar className="mx-auto text-4xl text-gray-300" />
            <p className="mt-3 font-semibold text-gray-800">Add a vehicle first</p>
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="mt-4 rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800"
            >
              Go to account
            </button>
          </div>
        ) : loading ? (
          <div className="flex justify-center rounded-xl border border-gray-200 bg-white py-12 shadow-sm">
            <FaSpinner className="animate-spin text-2xl text-gray-400" />
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">
            {error}
          </div>
        ) : (
          <PassportContent
            passport={passport}
            vehicles={vehicles}
            activeVehicleId={activeVehicleId}
            onSelectVehicle={handleSelectVehicle}
            onExport={handleExport}
            onShare={handleShare}
            exporting={exporting}
            sharing={sharing}
          />
        )}
      </div>
    </div>
  );
};

export { PassportContent, HealthGauge };
export default HealthPassport;
